import * as React from 'react';
import { wrap as wrapCls, spinner as spinnerCls } from './Spinner.css';

export type SpinnerSize = 'sm' | 'md' | 'lg';
export type SpinnerTone = 'inherit' | 'primary' | 'inverse';

export interface SpinnerProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: SpinnerSize;
  tone?: SpinnerTone;
  /** Accessible label. Defaults to "Loading". */
  label?: string;
  /** React 19 ref-as-prop. */
  ref?: React.Ref<HTMLSpanElement>;
}

const SIZE_MAP: Record<SpinnerSize, number> = { sm: 14, md: 18, lg: 24 };

/**
 * Spinner — circular loading indicator. Three sizes (14 / 18 / 24 px),
 * three tones (`inherit` / `primary` / `inverse`). Sets `role="status"`
 * + `aria-live="polite"` + `aria-label` so the loading state is
 * announced to screen readers; the SVG itself is `aria-hidden`.
 */
export function Spinner({
  size = 'md',
  tone = 'inherit',
  label = 'Loading',
  ref,
  className,
  ...rest
}: SpinnerProps) {
  const px = SIZE_MAP[size];
  const cls = [wrapCls({ tone }), className].filter(Boolean).join(' ');
  return (
    <span ref={ref} className={cls} role="status" aria-live="polite" aria-label={label} {...rest}>
      <svg
        className={spinnerCls}
        width={px}
        height={px}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          strokeOpacity="0.25"
          strokeWidth="2.5"
          fill="none"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </span>
  );
}
Spinner.displayName = 'Spinner';
